import type {
	FinanceBudget,
	FinanceBudgetSet,
	FinanceCategory,
	FinanceCategoryCreate,
	FinanceCategoryUpdate,
	FinanceDebt,
	FinanceDebtCreate,
	FinanceDebtUpdate,
	FinanceEntry,
	FinanceEntryCreate,
	FinanceEntryUpdate,
	FinanceGoalCreate,
	FinanceGoalUpdate,
	FinanceOverview,
	FinanceRecurring,
	FinanceRecurringCreate,
	FinanceRecurringUpdate,
	FinanceReport,
	FinanceWallet,
	FinanceWalletCreate,
	FinanceWalletUpdate
} from '@andresot/protocol/finance';
import { request } from './client.js';

/** Сводка заодно догоняет пропущенные регулярные платежи на сервере. */
export function fetchOverview(month?: string): Promise<{ overview: FinanceOverview }> {
	return request(`/api/finance/overview${month ? `?month=${month}` : ''}`);
}

export function fetchReport(month?: string): Promise<{ report: FinanceReport }> {
	return request(`/api/finance/report${month ? `?month=${month}` : ''}`);
}

export function createWallet(input: FinanceWalletCreate): Promise<{ wallets: FinanceWallet[] }> {
	return request('/api/finance/wallets', { body: input });
}

export function updateWallet(
	walletId: string,
	input: FinanceWalletUpdate
): Promise<{ wallets: FinanceWallet[] }> {
	return request(`/api/finance/wallets/${walletId}`, { method: 'PATCH', body: input });
}

export function deleteWallet(walletId: string): Promise<{ wallets: FinanceWallet[] }> {
	return request(`/api/finance/wallets/${walletId}`, { method: 'DELETE' });
}

export function createCategory(
	input: FinanceCategoryCreate
): Promise<{ categories: FinanceCategory[] }> {
	return request('/api/finance/categories', { body: input });
}

export function updateCategory(
	categoryId: string,
	input: FinanceCategoryUpdate
): Promise<{ categories: FinanceCategory[] }> {
	return request(`/api/finance/categories/${categoryId}`, { method: 'PATCH', body: input });
}

export function deleteCategory(categoryId: string): Promise<{ categories: FinanceCategory[] }> {
	return request(`/api/finance/categories/${categoryId}`, { method: 'DELETE' });
}

export function fetchEntries(query: {
	from?: string;
	to?: string;
	walletId?: string;
	categoryId?: string;
	kind?: 'expense' | 'income' | 'transfer';
}): Promise<{ entries: FinanceEntry[] }> {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
	return request(`/api/finance/entries?${params.toString()}`);
}

export function createEntry(input: FinanceEntryCreate): Promise<{ entry: FinanceEntry }> {
	return request('/api/finance/entries', { body: input });
}

export function updateEntry(
	entryId: string,
	input: FinanceEntryUpdate
): Promise<{ entry: FinanceEntry }> {
	return request(`/api/finance/entries/${entryId}`, { method: 'PATCH', body: input });
}

export function deleteEntry(entryId: string): Promise<{ ok: true }> {
	return request(`/api/finance/entries/${entryId}`, { method: 'DELETE' });
}

export function setBudget(
	categoryId: string,
	input: FinanceBudgetSet
): Promise<{ budgets: FinanceBudget[] }> {
	return request(`/api/finance/budgets/${categoryId}`, { method: 'PUT', body: input });
}

export function deleteBudget(categoryId: string): Promise<{ ok: true }> {
	return request(`/api/finance/budgets/${categoryId}`, { method: 'DELETE' });
}

export function createRecurring(
	input: FinanceRecurringCreate
): Promise<{ recurrings: FinanceRecurring[] }> {
	return request('/api/finance/recurrings', { body: input });
}

export function updateRecurring(
	recurringId: string,
	input: FinanceRecurringUpdate
): Promise<{ recurrings: FinanceRecurring[] }> {
	return request(`/api/finance/recurrings/${recurringId}`, { method: 'PATCH', body: input });
}

export function deleteRecurring(recurringId: string): Promise<{ recurrings: FinanceRecurring[] }> {
	return request(`/api/finance/recurrings/${recurringId}`, { method: 'DELETE' });
}

export function createGoal(input: FinanceGoalCreate): Promise<{ ok: true }> {
	return request('/api/finance/goals', { body: input });
}

export function updateGoal(goalId: string, input: FinanceGoalUpdate): Promise<{ ok: true }> {
	return request(`/api/finance/goals/${goalId}`, { method: 'PATCH', body: input });
}

export function deleteGoal(goalId: string): Promise<{ ok: true }> {
	return request(`/api/finance/goals/${goalId}`, { method: 'DELETE' });
}

export function createDebt(input: FinanceDebtCreate): Promise<{ debts: FinanceDebt[] }> {
	return request('/api/finance/debts', { body: input });
}

export function updateDebt(
	debtId: string,
	input: FinanceDebtUpdate
): Promise<{ debts: FinanceDebt[] }> {
	return request(`/api/finance/debts/${debtId}`, { method: 'PATCH', body: input });
}

export function deleteDebt(debtId: string): Promise<{ debts: FinanceDebt[] }> {
	return request(`/api/finance/debts/${debtId}`, { method: 'DELETE' });
}
